/**
 * useMintContract.js
 *
 * Reads live sale state from the deployed HexabotsNFT contract and sends
 * mint transactions.
 *
 * ── Built for mint-day load, not just correctness ────────────────────
 * A naive version of this hook (the one this replaces) made ~10 separate
 * eth_call requests every 10s from every open tab. At a few thousand
 * concurrent visitors that is thousands of RPC requests per second, and
 * any shared endpoint rate-limits long before the chain itself cares —
 * so the supply counter freezes and mints fail for everyone at exactly
 * the worst moment. Mitigations here, in order of impact:
 *
 *   1. ONE aggregated `saleState()` call per poll instead of ~10.
 *   2. Randomised jitter on every interval, so tabs that all loaded
 *      during the same announcement don't re-sync into one thundering
 *      herd hitting the RPC on the same tick forever after.
 *   3. Polling pauses entirely while the tab is hidden, and refreshes
 *      once on return — idle background tabs stop costing requests.
 *   4. Exponential backoff (to 2 min) on RPC failure, so a struggling
 *      endpoint gets relief instead of a retry storm, then resets on
 *      the first success.
 *   5. Endpoint failover across READ_RPC_URLS.
 *   6. Countdown timing is anchored to the chain's own block timestamp
 *      rather than the visitor's system clock.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ethers } from 'ethers';
import { CONTRACT_ADDRESS, CONTRACT_ABI } from '../lib/contractConfig';
import { ARC_TESTNET, READ_RPC_URLS } from '../lib/chain';
import { getProofFor } from '../lib/whitelist';

const BASE_POLL_MS = 12_000;
const MAX_BACKOFF_MS = 120_000;
const JITTER_RATIO = 0.35; // ±35% spread so polls fan out across the window

const isDeployed =
  CONTRACT_ADDRESS && CONTRACT_ADDRESS !== '0x0000000000000000000000000000000000000000';

export const PHASE = { NONE: 0, GTD: 1, PUBLIC: 2, SOLD_OUT: 3 };

/**
 * Pull a custom-error name out of a failed transaction.
 *
 * ethers only populates `err.revert` when it can decode the revert
 * itself. Going through a wallet provider it frequently cannot — the
 * error arrives wrapped in the wallet's own envelope — so `revert` is
 * null and the user sees the raw "missing revert data", which tells
 * them nothing. The 4-byte selector IS in there, just nested
 * differently depending on the wallet, so dig it out and decode it
 * against the ABI ourselves.
 */
const decodeRevert = (err) => {
  if (err?.revert?.name) return err.revert.name;
  if (err?.errorName) return err.errorName;
  const candidates = [
    err?.data,
    err?.info?.error?.data?.data,
    err?.info?.error?.data,
    err?.error?.data?.data,
    err?.error?.data,
    err?.cause?.data,
  ];
  for (const raw of candidates) {
    const hex = typeof raw === 'string' ? raw : raw?.data;
    if (typeof hex === 'string' && hex.startsWith('0x') && hex.length >= 10) {
      try {
        return new ethers.Interface(CONTRACT_ABI).parseError(hex)?.name;
      } catch { /* try the next shape */ }
    }
  }
  return null;
};

const friendlyError = (err) => {
  if (err?.code === 'ACTION_REJECTED' || err?.code === 4001) return 'Transaction rejected in wallet.';
  if (err?.code === 'INSUFFICIENT_FUNDS') return 'Insufficient balance to cover mint price + gas.';
  const name = decodeRevert(err);
  switch (name) {
    case 'PhaseNotOpen':      return 'That mint phase is not open yet.';
    case 'NotWhitelisted':    return 'This wallet is not on the GTD whitelist.';
    case 'ExceedsWalletCap':  return 'You have already minted your maximum for this phase.';
    case 'ExceedsPerTxCap':   return 'Too many in one transaction — lower the quantity.';
    case 'ExceedsMaxSupply':  return 'Not enough supply left for that quantity.';
    case 'IncorrectPayment':  return 'Price changed — refresh the page and try again.';
    case 'EnforcedPause':     return 'Minting is paused right now.';
    case 'CallerIsContract':  return 'Public mint requires a normal wallet, not a contract.';
    case 'ZeroQuantity':      return 'Quantity must be at least 1.';
    default: break;
  }
  // Still undecodable — say something useful rather than "missing revert data",
  // which reads like a network fault when it is almost always a rejected mint.
  const raw = err?.shortMessage || err?.reason || err?.message || '';
  if (/missing revert data|CALL_EXCEPTION|execution reverted/i.test(raw)) {
    return 'The contract rejected this mint — most often the per-wallet limit is already used, or the phase is not open for this wallet.';
  }
  return raw || 'Transaction failed.';
};

const withJitter = (ms) => Math.round(ms * (1 + (Math.random() * 2 - 1) * JITTER_RATIO));

export default function useMintContract(getSigner, address) {
  const [state, setState] = useState({
    loading: true,
    deployed: isDeployed,
    rpcHealthy: true,
    totalSupply: 0,
    maxSupply: 2222,
    mintPrice: 0n,
    gtdStartTime: 0,
    publicStartTime: 0,
    phase: PHASE.NONE,
    paused: false,
    revealed: false,
    maxPerWalletGTD: 1,
    maxPerWalletPublic: 2,
    maxPerTx: 5,
    userGtdMinted: 0,
    userPublicMinted: 0,
    owner: null,
    teamMinted: 0,
    /** Offset between chain time and this device's clock, in seconds.
     *  Countdowns add this so a wrong local clock can't misreport the
     *  time until a phase opens. */
    clockSkew: 0,
  });
  const [txState, setTxState] = useState({ pending: false, error: null, lastTxHash: null });
  const [whitelistStatus, setWhitelistStatus] = useState({ loading: false, checked: false, isWhitelisted: false, error: null });

  const rpcIndexRef = useRef(0);
  const providerRef = useRef(null);
  const backoffRef = useRef(0);
  const timerRef = useRef(null);
  const addressRef = useRef(address);
  addressRef.current = address;

  const getReadProvider = useCallback(() => {
    if (!providerRef.current) {
      const url = READ_RPC_URLS[rpcIndexRef.current % READ_RPC_URLS.length];
      providerRef.current = new ethers.JsonRpcProvider(url, ARC_TESTNET.chainIdDec, {
        staticNetwork: true, // skip the per-provider eth_chainId handshake
        // ethers v6 bundles concurrent calls into one array-style JSON-RPC
        // request by default (batchMaxCount: 100). Not every endpoint
        // accepts batch arrays — when one doesn't, every call in the batch
        // returns empty and surfaces as a bogus "missing revert data"
        // CALL_EXCEPTION. saleState() is a single call so this is belt-and-
        // braces, but it costs nothing and removes a whole failure mode.
        batchMaxCount: 1,
      });
    }
    return providerRef.current;
  }, []);

  const rotateRpc = useCallback(() => {
    rpcIndexRef.current += 1;
    providerRef.current = null;
  }, []);

  const refresh = useCallback(async () => {
    if (!isDeployed) {
      setState((s) => ({ ...s, loading: false, deployed: false }));
      return;
    }
    try {
      const contract = new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, getReadProvider());
      const s = await contract.saleState(addressRef.current || ethers.ZeroAddress);

      // owner()/teamMinted() aren't in saleState. Two extra reads, but only
      // meaningful to the team wallet, so they stay out of the aggregated
      // call that every visitor makes.
      // Both are public state and both are shown on the phase cards, so
      // they are read for every visitor. (teamMinted used to be fetched
      // only when the connected wallet was the owner, which left the
      // team phase permanently reading 0 / 222 for everyone else.)
      let owner = null, teamMinted = 0;
      try {
        [owner, teamMinted] = await Promise.all([
          contract.owner(),
          contract.teamMinted().then(Number),
        ]);
      } catch { /* non-fatal — the rest of the page works without it */ }

      backoffRef.current = 0; // healthy again
      setState({
        loading: false,
        deployed: true,
        rpcHealthy: true,
        totalSupply: Number(s.minted),
        maxSupply: Number(s.maxSupply),
        mintPrice: s.mintPrice,
        gtdStartTime: Number(s.gtdStart),
        publicStartTime: Number(s.publicStart),
        phase: Number(s.phase),
        paused: s.paused,
        revealed: s.revealed,
        maxPerWalletGTD: Number(s.gtdCap),
        maxPerWalletPublic: Number(s.publicCap),
        maxPerTx: Number(s.perTxCap),
        userGtdMinted: Number(s.userGtdMinted),
        userPublicMinted: Number(s.userPublicMinted),
        owner,
        teamMinted,
        clockSkew: Number(s.blockTime) - Math.floor(Date.now() / 1000),
      });
    } catch (err) {
      console.error('saleState read failed:', err);
      rotateRpc();
      backoffRef.current = backoffRef.current
        ? Math.min(backoffRef.current * 2, MAX_BACKOFF_MS)
        : BASE_POLL_MS * 2;
      setState((s) => ({ ...s, loading: false, rpcHealthy: false }));
    }
  }, [getReadProvider, rotateRpc]);

  // ── Poll loop: jittered, backoff-aware, paused while tab is hidden ──
  useEffect(() => {
    let cancelled = false;

    const schedule = () => {
      if (cancelled) return;
      const delay = withJitter(backoffRef.current || BASE_POLL_MS);
      timerRef.current = setTimeout(tick, delay);
    };

    const tick = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.hidden) {
        schedule(); // stay idle, just re-arm
        return;
      }
      await refresh();
      schedule();
    };

    refresh().then(schedule);

    const onVisibility = () => {
      if (!document.hidden) refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh]);

  // ── Whitelist lookup: one sharded fetch, only once a wallet connects ──
  useEffect(() => {
    let cancelled = false;
    if (!address) {
      setWhitelistStatus({ loading: false, checked: false, isWhitelisted: false, error: null });
      return;
    }
    setWhitelistStatus({ loading: true, checked: false, isWhitelisted: false, error: null });
    getProofFor(address)
      .then((proof) => {
        if (!cancelled) setWhitelistStatus({ loading: false, checked: true, isWhitelisted: Boolean(proof), error: null });
      })
      .catch((err) => {
        // Network failure ≠ not whitelisted. Kept distinct so a genuinely
        // whitelisted user is never told they are excluded because a
        // request dropped.
        if (!cancelled) setWhitelistStatus({ loading: false, checked: false, isWhitelisted: false, error: err.message });
      });
    return () => { cancelled = true; };
  }, [address]);

  // Synchronous submit lock — React state updates asynchronously, so a
  // double-click can slip two txs past a state-only guard before the
  // first re-render lands. A ref cannot race like that.
  const submittingRef = useRef(false);

  const runTx = useCallback(async (fn) => {
    if (submittingRef.current) throw new Error('A transaction is already in progress.');
    if (!isDeployed || !ethers.isAddress(CONTRACT_ADDRESS)) {
      throw new Error('Contract address is not configured yet.');
    }

    submittingRef.current = true;
    setTxState({ pending: true, error: null, lastTxHash: null });
    try {
      const signer = await getSigner(); // re-verifies live chain ID — see useWallet.js

      const code = await signer.provider.getCode(CONTRACT_ADDRESS);
      if (!code || code === '0x') {
        throw new Error('No contract found at the configured address on this network.');
      }

      const contract = new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, signer);
      const tx = await fn(contract);
      setTxState({ pending: true, error: null, lastTxHash: tx.hash });
      const receipt = await tx.wait();
      if (receipt.status !== 1) throw new Error('Transaction reverted on-chain.');
      setTxState({ pending: false, error: null, lastTxHash: tx.hash });
      await refresh();
      return tx;
    } catch (err) {
      const message = friendlyError(err);
      setTxState({ pending: false, error: message, lastTxHash: null });
      throw new Error(message);
    } finally {
      submittingRef.current = false;
    }
  }, [getSigner, refresh]);

  /**
   * Re-read the contract and check every condition publicMint/gtdMint
   * enforce, BEFORE sending anything.
   *
   * Necessary because ARC's RPC does not reliably return revert data on
   * a failed estimate — so the wallet surfaces a bare "missing revert
   * data" and there is no selector to decode. Checking the same
   * conditions here turns an opaque failure into a specific one, and
   * it uses freshly-read state rather than the polled copy, which can
   * be up to 12s stale.
   *
   * @returns {Promise<string|null>} reason the mint would fail, or null
   */
  const preflight = useCallback(async (kind, qty, account) => {
    const contract = new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, getReadProvider());
    const s = await contract.saleState(account || ethers.ZeroAddress);

    if (s.paused) return 'Minting is paused by the team right now.';

    const phase = Number(s.phase);
    const chainNow = Number(s.blockTime);

    if (kind === 'public') {
      if (Number(s.publicStart) === 0) return 'Public mint has not been scheduled yet.';
      if (chainNow < Number(s.publicStart)) {
        const mins = Math.ceil((Number(s.publicStart) - chainNow) / 60);
        return `Public mint is not open yet — it opens in about ${mins} minute${mins === 1 ? '' : 's'}.`;
      }
      if (qty > Number(s.perTxCap)) return `Maximum ${s.perTxCap} per transaction.`;
      const used = Number(s.userPublicMinted);
      const cap = Number(s.publicCap);
      // cap of 0 means UNLIMITED — matching the contract, which skips the
      // wallet check entirely when maxPerWalletPublic is 0. Treating 0 as
      // a literal ceiling blocks every mint before it is attempted.
      if (cap !== 0 && used + qty > cap) {
        return used >= cap
          ? `This wallet has already minted its limit of ${cap} in the public phase.`
          : `This wallet can mint ${cap - used} more in the public phase (limit ${cap}).`;
      }
    } else {
      if (Number(s.gtdStart) === 0) return 'The GTD phase has not been scheduled yet.';
      if (chainNow < Number(s.gtdStart)) return 'The GTD phase is not open yet.';
      const used = Number(s.userGtdMinted);
      const cap = Number(s.gtdCap);
      if (cap !== 0 && used + qty > cap) {
        return used >= cap
          ? `This wallet has already minted its limit of ${cap} in the GTD phase.`
          : `This wallet can mint ${cap - used} more in the GTD phase (limit ${cap}).`;
      }
    }

    if (Number(s.minted) + qty > Number(s.maxSupply)) {
      return `Only ${Number(s.maxSupply) - Number(s.minted)} left in the collection.`;
    }

    // Balance must cover price AND leave something for gas.
    if (account) {
      const cost = s.mintPrice * BigInt(qty);
      const bal = await getReadProvider().getBalance(account);
      if (bal < cost) {
        return `Not enough balance — this mint costs ${ethers.formatEther(cost)} USDC plus gas.`;
      }
    }

    if (phase === 3) return 'The collection is sold out.';
    return null;
  }, [getReadProvider]);

  const mintGTD = useCallback(async (qty, account) => {
    if (!Number.isInteger(qty) || qty <= 0) throw new Error('Invalid quantity.');
    const blocked = await preflight('gtd', qty, account);
    if (blocked) throw new Error(blocked);
    const proof = await getProofFor(account);
    if (!proof) throw new Error('This wallet is not on the GTD whitelist.');
    return runTx((contract) => contract.gtdMint(qty, proof, { value: state.mintPrice * BigInt(qty) }));
  }, [runTx, state.mintPrice, preflight]);

  const mintPublic = useCallback(async (qty, account) => {
    if (!Number.isInteger(qty) || qty <= 0) throw new Error('Invalid quantity.');
    const blocked = await preflight('public', qty, account);
    if (blocked) throw new Error(blocked);
    return runTx((contract) => contract.publicMint(qty, { value: state.mintPrice * BigInt(qty) }));
  }, [runTx, state.mintPrice, preflight]);

  const mintTeam = useCallback((to, qty) => {
    if (!ethers.isAddress(to)) return Promise.reject(new Error('Invalid recipient address.'));
    if (!Number.isInteger(qty) || qty <= 0) return Promise.reject(new Error('Invalid quantity.'));
    return runTx((contract) => contract.teamMint(to, qty));
  }, [runTx]);

  return {
    ...state,
    txState,
    whitelistStatus,
    refresh,
    preflight,
    mintGTD,
    mintPublic,
    mintTeam,
  };
}
